import React, { createContext, useContext, useState, useCallback } from "react";
import { nanoid } from "nanoid";

const MessageContext = createContext();

export const useMessages = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessages must be used within a MessageProvider");
  }
  return context;
};

export const MessageProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const addMessage = useCallback(
    ({ message, type = "info", timestamp = new Date() }) => {
      const id = nanoid();
      const msg = { id, message, type, timestamp };

      setMessages((prev) => [...prev, msg]);

      return id;
    },
    []
  );

  const removeMessage = useCallback((id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Convenience methods
  const success = useCallback(
    (message, options = {}) =>
      addMessage({ message, type: "success", ...options }),
    [addMessage]
  );

  const error = useCallback(
    (message, options = {}) =>
      addMessage({ message, type: "error", ...options }),
    [addMessage]
  );

  const warning = useCallback(
    (message, options = {}) =>
      addMessage({ message, type: "warning", ...options }),
    [addMessage]
  );

  const info = useCallback(
    (message, options = {}) => addMessage({ message, type: "info", ...options }),
    [addMessage]
  );

  // Get the latest message for banner display
  const latestMessage = messages[messages.length - 1];

  const value = {
    messages,
    latestMessage,
    addMessage,
    removeMessage,
    clearMessages,
    isExpanded,
    toggleExpanded,
    success,
    error,
    warning,
    info,
  };

  return (
    <MessageContext.Provider value={value}>{children}</MessageContext.Provider>
  );
};
